/**
 * The gym's brand colour at runtime (CLAUDE.md "UI tokens"): gyms.brand_colour overrides
 * the --primary family. Status colours never change.
 *
 * A gym can pick any colour, including one white text cannot sit on, so the colour is
 * adjusted only as far as needed to keep text on a primary button at 4.5 : 1
 * (UX-PRINCIPLES §5): darkened in light mode, lightened in dark mode. The hue stays.
 */

export type Theme = 'light' | 'dark';

interface Hsl {
  h: number;
  s: number;
  l: number;
}

export function hexToHsl(hex: string): Hsl | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;
  const n = parseInt(match[1]!, 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function hslToRgb({ h, s, l }: Hsl): [number, number, number] {
  const sat = s / 100;
  const light = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sat * Math.min(light, 1 - light);
  const f = (n: number) => light - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [f(0), f(8), f(4)];
}

function luminance(hsl: Hsl): number {
  const [r, g, b] = hslToRgb(hsl).map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)) as [
    number,
    number,
    number,
  ];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a: Hsl, b: Hsl): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

const WHITE: Hsl = { h: 0, s: 0, l: 100 };
const NEAR_BLACK: Hsl = { h: 144, s: 61, l: 9 };
const channels = ({ h, s, l }: Hsl) => `${h} ${s}% ${l}%`;

/** The --primary family for a brand colour, or null to keep the defaults. */
export function brandTokens(hex: string | null | undefined, theme: Theme): Record<string, string> | null {
  const base = hex ? hexToHsl(hex) : null;
  if (!base) return null;
  const primary = { ...base };

  if (theme === 'light') {
    while (contrast(primary, WHITE) < 4.5 && primary.l > 5) primary.l -= 1;
    return {
      '--primary': channels(primary),
      '--primary-hover': channels({ ...primary, l: Math.max(primary.l - 6, 3) }),
      '--primary-subtle': channels({ h: primary.h, s: Math.min(primary.s, 70), l: 94 }),
      '--primary-ink': channels(WHITE),
    };
  }
  primary.l = Math.max(primary.l, 45);
  while (contrast(primary, NEAR_BLACK) < 4.5 && primary.l < 95) primary.l += 1;
  return {
    '--primary': channels(primary),
    '--primary-hover': channels({ ...primary, l: Math.min(primary.l + 8, 97) }),
    '--primary-subtle': channels({ h: primary.h, s: Math.min(primary.s, 40), l: 14 }),
    '--primary-ink': channels({ ...NEAR_BLACK, h: primary.h }),
  };
}

const KEYS = ['--primary', '--primary-hover', '--primary-subtle', '--primary-ink'];

/** Applies (or with null, removes) the brand on <html>. */
export function applyBrand(hex: string | null | undefined, theme: Theme): void {
  const root = document.documentElement;
  const tokens = brandTokens(hex, theme);
  for (const key of KEYS) {
    if (tokens) root.style.setProperty(key, tokens[key]!);
    else root.style.removeProperty(key);
  }
}
